export interface CompressionOptions {
  quality?: number; // 0.1 to 1.0, defaults to 0.8
  maxWidth?: number; // Max width in pixels, defaults to 1200
  maxHeight?: number; // Max height in pixels, defaults to 1200
  outputFormat?: 'jpeg' | 'webp'; // Output format, defaults to 'jpeg'
}

export async function compressImage(
  imageUrl: string,
  options: CompressionOptions = {}
): Promise<string> {
  const {
    quality = 0.8,
    maxWidth = 1200,
    maxHeight = 1200,
    outputFormat = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Unable to get canvas context'));
          return;
        }

        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = calculateDimensions(img.width, img.height, maxWidth, maxHeight);
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed format
        const mimeType = outputFormat === 'jpeg' ? 'image/jpeg' : 'image/webp';
        const compressedDataUrl = canvas.toDataURL(mimeType, quality);
        
        resolve(compressedDataUrl);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = (error) => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
}

function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Calculate scaling factor
  const scaleX = maxWidth / width;
  const scaleY = maxHeight / height;
  const scale = Math.min(scaleX, scaleY, 1); // Don't upscale

  // Apply scaling
  width = Math.round(width * scale);
  height = Math.round(height * scale);

  return { width, height };
}

export async function compressImageFile(
  file: File,
  options: CompressionOptions = {}
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      if (!e.target?.result) {
        reject(new Error('Failed to read file'));
        return;
      }
      
      try {
        const dataUrl = e.target.result as string;
        const compressed = await compressImage(dataUrl, options);
        resolve(compressed);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
}